import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
  IonLoading,
} from "@ionic/react";
// import "./Page.css";
import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Stack,
  Grid,
  Card,
  Typography,
  Box,
  Chip,
  CardContent,
  Divider,
} from "@mui/material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../../common/utils";
import { Link } from "react-router-dom";

const JobSetup: React.FC = () => {
  // Project States
  const [projectName, setProjectName] = useState("");
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [budget, setBudget] = useState("");
  const [projectData, setProjectData] = useState([]);
  const [formError, setFormError] = useState("");
  const [status, setStatus] = useState({
    loading: false,
    error: false,
    message: "",
  });

  const addTag = () => {
    if (tagInput && !projectTags.includes(tagInput)) {
      setProjectTags([...projectTags, tagInput]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setProjectTags(projectTags.filter((t) => t !== tag));
  };

  async function createProject() {
    // Basic form validation
    if (!projectName || projectTags.length === 0 || !budget) {
      setFormError("Please fill all fields before creating a project.");
      return;
    }

    setFormError("");
    setStatus({ loading: true, error: false, message: "Creating project..." });

    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("name", projectName);
    formData.append("tags", projectTags.toString());
    formData.append("budgetcosts", budget);

    try {
      const response = await axios.post(
        baseUrl() + "projects/addProject",
        formData,
        config
      );
      console.log(response);
      setStatus({ loading: false, error: false, message: "" });
      getAllProject(); // Refresh list
      setProjectName("");
      setBudget("");
      setProjectTags([]);
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to create project",
      });
    }
  }

  useEffect(() => {
    getAllProject();
    getAllStaffs();
  }, []);

  async function getAllProject() {
    setStatus({ loading: true, error: false, message: "Fetching projects..." });

    const formData = new FormData();
    formData.append("requestID", requestID());

    try {
      const response = await axios.post(
        baseUrl() + "projects/getAllProjects",
        formData,
        config
      );
      console.log(response.data.data);
      setProjectData(response.data.data);
      setStatus({ loading: false, error: false, message: "" });
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to load projects",
      });
    }
  }

  async function getAllStaffs() {
    setStatus({ loading: true, error: false, message: "Fetching staffs..." });

    const formData = new FormData();
    formData.append("requestID", requestID());

    try {
      const response = await axios.post(
        baseUrl() + "projects/getStaffs_new",
        formData,
        config
      );
      console.log("Staffs", response);
      // setProjectData(response.data.data);
      setStatus({ loading: false, error: false, message: "" });
    } catch (error) {
      console.error(error);
      setStatus({
        loading: false,
        error: true,
        message: "Failed to load projects",
      });
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Job Setup</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Job Setup</IonTitle>
          </IonToolbar>
        </IonHeader>

        <Box p={2}>
          {/* Ion Loading Spinner */}
          <IonLoading
            isOpen={status.loading}
            message={status.message || "Please wait..."}
            spinner="dots"
          />

          {/* Project Form Section */}
          <Typography variant="h6" gutterBottom>
            Project Management
          </Typography>
          <Box mb={2} p={2} borderRadius={2} boxShadow={1} bgcolor="#f9f9f9">
            <TextField
              fullWidth
              size="small"
              label="Project Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              margin="dense"
              error={!!formError && !projectName}
            />

            <Stack direction="row" spacing={1} my={1}>
              <TextField
                label="Add Tag"
                size="small"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
              />
              <Button onClick={addTag} variant="contained" size="small">
                Add Tag
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
              {projectTags.map((tag) => (
                <Chip key={tag} label={tag} onDelete={() => removeTag(tag)} />
              ))}
            </Stack>

            <TextField
              fullWidth
              size="small"
              label="Budget"
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              margin="dense"
              error={!!formError && !budget}
            />

            {/* Show form error message */}
            {formError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {formError}
              </Typography>
            )}

            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={createProject}
              >
                Create Project
              </Button>
            </Box>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 3 }} />

          {/* Project List */}
          <Typography variant="h6" gutterBottom>
            All Projects
          </Typography>
          <Grid container spacing={2}>
            {projectData.map((data, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Link
                  to={{
                    pathname: "/folder/projectDetail/",
                    search: `?id=${data.project_id}`,
                    state: { data },
                  }}
                  style={{ textDecoration: "none" }}
                >
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: 3,
                      height: "100%",
                      transition: "0.3s",
                      "&:hover": {
                        boxShadow: 6,
                      },
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {data.project_name}
                      </Typography>

                      <Box mt={1}>
                        <Typography variant="body2" color="text.secondary">
                          Jobs Available:
                        </Typography>

                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                          {typeof data.tags === "string" &&
                            data.tags
                              .split(",")
                              .map((tag, index) => (
                                <Chip
                                  key={index}
                                  label={tag.trim()}
                                  size="small"
                                />
                              ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Link>
              </Grid>
            ))}
          </Grid>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default JobSetup;